import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const STATUS_OPTIONS = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "blocked", label: "Blocked" },
  { value: "override", label: "Override" },
];

const getSummaryChipProps = (summary) => {
  const status = String((summary && summary.closeout_status) || "").toLowerCase();
  if (status === "complete") return { label: "Smoke tests complete", color: "success" };
  if (status === "blocked") return { label: "Smoke tests blocked", color: "error" };
  if (status === "incomplete") return { label: "Smoke tests incomplete", color: "warning" };
  if (status === "no_templates") return { label: "No templates", color: "default" };
  return { label: "Smoke tests pending", color: "default" };
};

const buildResultDrafts = (templates, results) => {
  const byKey = {};
  (results || []).forEach((row) => {
    if (!row || !row.template_key) return;
    byKey[row.template_key] = row;
  });

  return (templates || []).reduce((acc, template) => {
    const templateKey = template.template_key;
    const existing = byKey[templateKey] || {};
    acc[templateKey] = {
      id: existing.id || "",
      template_id: template.id || existing.template_id || "",
      template_key: templateKey,
      module_name: template.module_name || existing.module_name || "",
      status: existing.status || "pass",
      actual_result: existing.actual_result || "",
      notes: existing.notes || "",
      override_reason: existing.override_reason || "",
    };
    return acc;
  }, {});
};

export default function FullChangeSmokeTestPanel({ fullChangeId, moduleName, visible = true }) {
  const [templates, setTemplates] = useState([]);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadSmokeTests = useCallback(async () => {
    if (!fullChangeId || !visible) return;
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const params = new URLSearchParams({ full_change_id: String(fullChangeId) });
      if (moduleName) params.set("module_name", moduleName);

      const response = await fetch(`/api/dev-ide/full-change/smoke-tests/summary?${params.toString()}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || (data.errors || []).join("; ") || "Smoke test summary failed to load.");
      }

      const nextTemplates = data.templates || [];
      const nextResults = data.results || [];
      setTemplates(nextTemplates);
      setResults(nextResults);
      setSummary(data.summary || null);
      setDrafts(buildResultDrafts(nextTemplates, nextResults));
    } catch (err) {
      setError(err.message || "Smoke test summary failed to load.");
    } finally {
      setLoading(false);
    }
  }, [fullChangeId, moduleName, visible]);

  useEffect(() => {
    loadSmokeTests();
  }, [loadSmokeTests]);

  const summaryChip = useMemo(() => getSummaryChipProps(summary), [summary]);

  const updateDraft = (templateKey, field, value) => {
    setDrafts((current) => ({
      ...current,
      [templateKey]: {
        ...(current[templateKey] || { template_key: templateKey }),
        [field]: value,
      },
    }));
  };

  const saveTemplateResult = async (template) => {
    const templateKey = template.template_key;
    const draft = drafts[templateKey] || {};
    if (draft.status === "override" && !String(draft.override_reason || "").trim()) {
      setError("Override reason is required when status is Override.");
      return;
    }

    setSavingKey(templateKey);
    setError("");
    setMessage("");

    try {
      const payload = {
        full_change_id: fullChangeId,
        results: [
          {
            id: draft.id || undefined,
            template_id: draft.template_id || template.id,
            template_key: templateKey,
            module_name: draft.module_name || template.module_name || moduleName || "",
            status: draft.status || "pass",
            actual_result: draft.actual_result || "",
            notes: draft.notes || "",
            override_reason: draft.override_reason || "",
          },
        ],
      };

      const response = await fetch("/api/dev-ide/full-change/smoke-tests/results", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || (data.errors || []).join("; ") || "Smoke test result failed to save.");
      }

      setTemplates(data.templates || templates);
      setResults(data.results || []);
      setSummary(data.summary || null);
      setDrafts(buildResultDrafts(data.templates || templates, data.results || []));
      setMessage("Smoke test result saved.");
    } catch (err) {
      setError(err.message || "Smoke test result failed to save.");
    } finally {
      setSavingKey("");
    }
  };

  if (!visible || !fullChangeId) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
          <Box>
            <Typography variant="h6">Full Change smoke tests</Typography>
            <Typography variant="body2" color="text.secondary">
              Record operator feature checks after Codex automation, diff review, build/restart, and health checks.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color={summaryChip.color} label={summaryChip.label} />
            <Button size="small" variant="outlined" onClick={loadSmokeTests} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading smoke tests...</Typography>
          </Stack>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="success">{message}</Alert>}

        {summary && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`Templates: ${summary.template_count || 0}`} />
            <Chip size="small" variant="outlined" label={`Results: ${summary.result_count || 0}`} />
            <Chip size="small" color="success" variant="outlined" label={`Pass: ${summary.passed_count || 0}`} />
            <Chip size="small" color="error" variant="outlined" label={`Fail: ${summary.failed_count || 0}`} />
            <Chip size="small" color="warning" variant="outlined" label={`Blocked: ${summary.blocked_count || 0}`} />
            <Chip size="small" color="info" variant="outlined" label={`Override: ${summary.override_count || 0}`} />
          </Stack>
        )}

        {!loading && templates.length === 0 && (
          <Alert severity="info">
            No active smoke test templates are configured for this Full Change/module yet. This does not block build or backend verification actions.
          </Alert>
        )}

        {templates.map((template) => {
          const templateKey = template.template_key;
          const draft = drafts[templateKey] || {};
          const isSaving = savingKey === templateKey;
          return (
            <Paper key={templateKey} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{template.title}</Typography>
                    {template.description && (
                      <Typography variant="body2" color="text.secondary">{template.description}</Typography>
                    )}
                  </Box>
                  <Chip size="small" variant="outlined" label={template.module_name || "All modules"} />
                </Stack>

                {template.expected_result && (
                  <Alert severity="info" variant="outlined">
                    <strong>Expected result:</strong> {template.expected_result}
                  </Alert>
                )}

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={draft.status || "pass"}
                    onChange={(event) => updateDraft(templateKey, "status", event.target.value)}
                    sx={{ minWidth: 160 }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Actual result"
                    value={draft.actual_result || ""}
                    onChange={(event) => updateDraft(templateKey, "actual_result", event.target.value)}
                    fullWidth
                  />
                </Stack>

                <TextField
                  size="small"
                  label="Notes"
                  value={draft.notes || ""}
                  onChange={(event) => updateDraft(templateKey, "notes", event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />

                {draft.status === "override" && (
                  <TextField
                    size="small"
                    label="Override reason"
                    value={draft.override_reason || ""}
                    onChange={(event) => updateDraft(templateKey, "override_reason", event.target.value)}
                    required
                    multiline
                    minRows={2}
                    fullWidth
                    helperText="Required when recording an override. Use only when the operator accepts the residual risk."
                  />
                )}

                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => saveTemplateResult(template)}
                    disabled={isSaving || loading}
                  >
                    {isSaving ? "Saving..." : "Save Result"}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}
